import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSun } from 'react-icons/fi';
import { useTheme } from '../../contexts/ThemeContext';
import WidgetHeader from './WidgetHeader';
import { format } from 'date-fns';

interface ForecastWidgetProps { data: any | null; loading: boolean }

const ForecastWidget: React.FC<ForecastWidgetProps> = ({ data, loading }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  if (loading) return (
    <div className={`rounded-lg border-l-4 border-emerald-500 p-3 ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
      <WidgetHeader compact icon={<FiSun className="w-3.5 h-3.5 text-emerald-400" />} title="Forecast" accentColor="emerald" />
      <div className={`h-8 animate-pulse rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} />
    </div>
  );

  if (!data) return (
    <div className={`rounded-lg border-l-4 border-emerald-500 p-2 ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
      <WidgetHeader compact icon={<FiSun className="w-3.5 h-3.5 text-emerald-400" />} title="Forecast" accentColor="emerald" subtitle="No data" />
    </div>
  );

  const fmtDate = (d: string | undefined) => d ? format(new Date(d), 'MMM d') : '\u2014';
  const onTrack = data.on_track ?? data.status === 'on_track';

  return (
    <div className={`rounded-lg border-l-4 border-emerald-500 p-3 ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
      <WidgetHeader
        compact
        icon={<FiSun className="w-3.5 h-3.5 text-emerald-400" />}
        title="Forecast"
        accentColor="emerald"
        subtitle={onTrack ? '✓ On track' : '⚠ At risk'}
        actions={
          <button onClick={() => navigate('/work/chat')} className="p-1 text-[10px] text-emerald-400 hover:text-emerald-300 font-medium">
            Details &rarr;
          </button>
        }
      />
      <div className="space-y-1.5">
        <div className="flex justify-between text-xs">
          <span className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Optimistic</span>
          <span className="text-emerald-400 font-mono">{fmtDate(data.optimistic)}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Expected</span>
          <span className="text-emerald-300 font-mono font-bold">{fmtDate(data.expected)}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Pessimistic</span>
          <span className="text-emerald-400 font-mono">{fmtDate(data.pessimistic)}</span>
        </div>
      </div>
    </div>
  );
};

export default ForecastWidget;
